import React from 'react';
import { Check, Plus, X } from '@phosphor-icons/react';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';
import type { JobMatchResult } from '../../utils/cv-check';

const MAX_CHIPS = 8;

export interface CvMatchResultProps {
  result: JobMatchResult;
  /** Adds a skill the CV text already mentions to the skills list; only on the person's click. */
  onAddSkill?: (skill: string) => void;
  headingId: string;
}

/**
 * The CV-to-job comparison as chips and checks (TEC-711): matched, mentioned-but-not-listed,
 * missing, required languages and years. Shared by the job page card and the profile page's
 * "check against a job ad", so both read the same way.
 */
export function CvMatchResult({ result, onAddSkill, headingId }: CvMatchResultProps) {
  const formatMessage = useFormatMessage();
  const total = result.matched.length + result.mentioned.length + result.missing.length;
  const have = result.matched.length + result.mentioned.length;
  const addSkill = (skill: string) => onAddSkill?.(skill);

  return (
    <>
      {total > 0 ? (
        <>
          <div className="mt-1 flex items-baseline gap-2">
            <h2 id={headingId} className="text-lg font-bold text-[#16150F] dark:text-[#F3EFE4]">
              <Msg
                id="cvJobMatch.skillsHeadline"
                defaultMessage="Your CV shows {have} of the {total} skills this job lists"
                description="Headline of the job page CV match card; have/total are counts of the job's listed skills"
                values={{ have, total }}
              />
            </h2>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#E6DFD3] dark:bg-[#3A2A18]" aria-hidden>
            <div className="h-full rounded-full bg-[#3B5E45] dark:bg-[#98CFA8]" style={{ width: `${Math.round((have / total) * 100)}%` }} />
          </div>

          {result.matched.length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-1.5" aria-label={formatMessage({ id: 'cvJobMatch.matchedLabel', defaultMessage: 'Skills on your CV', description: 'Accessible label for the list of matched skills' })}>
              {result.matched.slice(0, MAX_CHIPS).map((s) => (
                <li key={s} className="inline-flex items-center gap-1 rounded-full bg-[#E3EDE5] px-2.5 py-1 text-xs font-semibold text-[#2A4533] dark:bg-[#1C2A20] dark:text-[#98CFA8]">
                  <Check className="h-3 w-3" weight="bold" aria-hidden />
                  {s}
                </li>
              ))}
            </ul>
          ) : null}

          {result.mentioned.length > 0 ? (
            <div className="mt-3">
              <p className="text-xs font-semibold text-[#4A4337] dark:text-[#C9C0AE]">
                <Msg
                  id="cvJobMatch.mentionedIntro"
                  defaultMessage="Your CV mentions these but doesn't list them as skills. Add them so recruiters and search see them:"
                  description="Intro above the skills the CV text mentions but the skills list doesn't include"
                />
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {result.mentioned.slice(0, MAX_CHIPS).map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => addSkill(s)}
                      className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-dashed border-[#3B5E45] px-2.5 text-xs font-semibold text-[#2A4533] hover:bg-[#E3EDE5] dark:border-[#98CFA8] dark:text-[#98CFA8] dark:hover:bg-[#1C2A20]"
                      aria-label={formatMessage({ id: 'cvJobMatch.addSkillLabel', defaultMessage: 'Add {skill} to your CV skills', description: 'Accessible label of the button that adds a skill to the CV' }, { skill: s })}
                    >
                      <Plus className="h-3 w-3" weight="bold" aria-hidden />
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.missing.length > 0 ? (
            <div className="mt-3">
              <p className="text-xs font-semibold text-[#4A4337] dark:text-[#C9C0AE]">
                <Msg id="cvJobMatch.missingIntro" defaultMessage="Not on your CV:" description="Intro above the job's skills the CV doesn't mention" />
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {result.missing.slice(0, MAX_CHIPS).map((s) => (
                  <li key={s} className="rounded-full bg-[#EFEAE1] px-2.5 py-1 text-xs text-[#6E6659] dark:bg-[#2A1D0F] dark:text-[#B0A896]">
                    {s}
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[11px] text-[#8A8173] dark:text-[#8A7060]">
                <Msg
                  id="cvJobMatch.missingHonesty"
                  defaultMessage="If you have one of these, add it to your CV with where you used it. If not, the job may still be worth applying for: ads list wishes, not only must-haves."
                  description="Note under the missing skills on the job page CV match card"
                />
              </p>
            </div>
          ) : null}
        </>
      ) : (
        <h2 id={headingId} className="mt-1 text-lg font-bold text-[#16150F] dark:text-[#F3EFE4]">
          <Msg id="cvJobMatch.requirementsHeadline" defaultMessage="Requirements against your CV" description="Headline of the job page CV match card when the job lists no skills, only languages or experience" />
        </h2>
      )}

      {result.requiredLanguages.length > 0 || result.requiredYears ? (
        <ul className="mt-4 space-y-1.5 border-t border-[#E6DFD3] pt-3 text-sm dark:border-[#3A2A18]">
          {result.requiredLanguages.map((l) => (
            <li key={l.name} className="flex items-center gap-2 text-[#16150F] dark:text-[#F3EFE4]">
              {l.met ? (
                <Check className="h-4 w-4 shrink-0 text-[#3B5E45] dark:text-[#98CFA8]" weight="bold" aria-hidden />
              ) : (
                <X className="h-4 w-4 shrink-0 text-[#B5452F] dark:text-[#E57373]" weight="bold" aria-hidden />
              )}
              {l.met ? (
                <Msg id="cvJobMatch.languageMet" defaultMessage="{language} required: on your CV" description="Job page CV match: a required language the CV lists" values={{ language: l.name }} />
              ) : (
                <Msg id="cvJobMatch.languageMissing" defaultMessage="{language} required: not on your CV" description="Job page CV match: a required language the CV does not list" values={{ language: l.name }} />
              )}
            </li>
          ))}
          {result.requiredYears ? (
            <li className="flex items-center gap-2 text-[#16150F] dark:text-[#F3EFE4]">
              {result.cvYears !== null && result.cvYears >= result.requiredYears ? (
                <Check className="h-4 w-4 shrink-0 text-[#3B5E45] dark:text-[#98CFA8]" weight="bold" aria-hidden />
              ) : (
                <span className="inline-block h-4 w-4 shrink-0 text-center text-xs font-bold leading-4 text-[#8A8173]" aria-hidden>
                  ?
                </span>
              )}
              {result.cvYears !== null ? (
                <Msg
                  id="cvJobMatch.years"
                  defaultMessage="Asks for {required}+ years of experience. Your CV shows about {years}."
                  description="Job page CV match: required years of experience vs years on the CV"
                  values={{ required: result.requiredYears, years: result.cvYears }}
                />
              ) : (
                <Msg
                  id="cvJobMatch.yearsUnknown"
                  defaultMessage="Asks for {required}+ years of experience. Add dates to your roles to compare."
                  description="Job page CV match: required years, when the CV has no readable dates"
                  values={{ required: result.requiredYears }}
                />
              )}
            </li>
          ) : null}
        </ul>
      ) : null}

    </>
  );
}
